"use client";

import { CaretDown, ListBullets } from "@repo/ui/components/icons";
import { cn } from "@repo/ui/lib/utils";
import { useEffect, useRef, useState } from "react";

import type { TocHeading } from "../../_components/RichText";
import { faNumber } from "../../_components/blog-format";

function useActiveHeading(headings: TocHeading[]) {
  const [active, setActive] = useState<string | null>(null);
  useEffect(() => {
    const elements = headings.map((h) => document.getElementById(h.id)).filter((el): el is HTMLElement => el !== null);
    if (!elements.length) return;
    // The active section is the last heading that has scrolled past the
    // upper part of the viewport (below the floating header).
    const onScroll = () => {
      let current: string | null = null;
      for (const el of elements) {
        if (el.getBoundingClientRect().top <= 140) current = el.id;
        else break;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [headings]);
  return active;
}

function TocList({ headings, active, onPick }: { headings: TocHeading[]; active: string | null; onPick?: () => void }) {
  return (
    <ol className="flex flex-col">
      {headings.map((h) => (
        <li key={h.id}>
          <a
            href={`#${h.id}`}
            onClick={onPick}
            aria-current={active === h.id ? "location" : undefined}
            className={cn(
              "block border-s-2 py-1.5 text-sm leading-6 transition-colors",
              h.level === 3 ? "ps-6" : "ps-3",
              active === h.id
                ? "border-foreground text-foreground font-bold"
                : "text-muted-foreground hover:text-foreground border-transparent",
            )}
          >
            {h.text}
          </a>
        </li>
      ))}
    </ol>
  );
}

/** Desktop: a card in the sticky side column, tracking the section in view. */
export function PostTocCard({ headings }: { headings: TocHeading[] }) {
  const active = useActiveHeading(headings);
  return (
    <nav aria-label="فهرست مطالب" className="bg-background rounded-card p-4 shadow-sm">
      <p className="mb-2 flex items-center gap-1.5 text-sm font-bold">
        <ListBullets size={18} />
        فهرست مطالب
      </p>
      <div className="max-h-[50vh] overflow-y-auto [scrollbar-width:thin]">
        <TocList headings={headings} active={active} />
      </div>
    </nav>
  );
}

/** Phones: a collapsed pill above the article that opens into the list and
 *  closes again once a section is picked. */
export function PostTocDisclosure({ headings }: { headings: TocHeading[] }) {
  const active = useActiveHeading(headings);
  const ref = useRef<HTMLDetailsElement>(null);
  return (
    <details ref={ref} className="group bg-background mb-3 rounded-card shadow-sm">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 text-sm font-bold [&::-webkit-details-marker]:hidden">
        <span className="flex items-center gap-1.5">
          <ListBullets size={18} />
          فهرست مطالب
          <span className="text-muted-foreground text-xs font-normal">{faNumber(headings.length)} بخش</span>
        </span>
        <CaretDown size={16} className="text-muted-foreground transition-transform group-open:rotate-180" />
      </summary>
      <div className="px-4 pb-3">
        <TocList headings={headings} active={active} onPick={() => ref.current?.removeAttribute("open")} />
      </div>
    </details>
  );
}
